import moment from 'moment'
import { v4 as uuidv4 } from 'uuid'
import { type FirebaseApp, initializeApp } from 'firebase/app'

import {
  type Firestore,
  doc,
  limit,
  query,
  where,
  getDoc,
  setDoc,
  getDocs,
  orderBy,
  updateDoc,
  deleteDoc,
  collection,
  writeBatch,
  startAfter,
  getFirestore,
  runTransaction,
  QueryConstraint,
  initializeFirestore,
  DocumentSnapshot,
} from 'firebase/firestore'

import { Logger } from '@utils/log'
import { ErrorCodes, ErrorService } from '@utils/error'
import { Collections } from '@constants/api'

import {
  CreateClientType,
  GetClientsType,
  ClientBaseType,
  ClientFirebaseType,
  UpdateClientType,
  ClientType,
  CreateStatusClientType,
  StatusClientType,
  StatusUpdateClientType,
} from '@customTypes/client'

import {
  CreateEventStatusType,
  CreateEventType,
  CreateEventTypeType,
  DeleteEventsType,
  EventBaseType,
  EventFirebaseType,
  EventTypeType,
  GetEventsType,
  StatusEventType,
  UpdateEventType,
} from '@customTypes/event'

import ClientEntityDS from '@api/domain/ds/ClientEntityDS'
import { parseRefToEntity } from '@utils/firebase'
import UserImpl from './UserImpl'

const ConfigCredentials = {
  firebaseConfig: {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
  },
}

class ClientEntityImpl extends ClientEntityDS {
  db: Firestore
  private app: FirebaseApp
  private userService = UserImpl.getInstance()

  static instance: ClientEntityImpl

  constructor() {
    super()
    this.app = initializeApp(ConfigCredentials.firebaseConfig)
    this.db = this.buildFirestore()
  }

  public static getInstance() {
    if (!this.instance) {
      this.instance = new ClientEntityImpl()
    }

    return this.instance
  }

  buildFirestore() {
    try {
      return initializeFirestore(this.app, {
        ignoreUndefinedProperties: true,
        experimentalForceLongPolling: false,
      })
    } catch (error) {
      return getFirestore(this.app)
    }
  }

  //Clients
  async paseToEventBase(eventFirebase: EventFirebaseType) {
    const {
      uid,
      attachments,
      date,
      description,
      contacts,
      name,
      amount,
      deleted,
      createdAt,
      updatedAt,
      client,
    } = eventFirebase

    const clientData = await parseRefToEntity<ClientFirebaseType>(client)

    let typeData: EventTypeType | null = null

    if (eventFirebase.type) {
      const typeSnap = await getDoc(eventFirebase.type)
      if (typeSnap.exists()) {
        typeData = typeSnap.data() as EventTypeType
      }
    }

    let statusData: StatusEventType | null = null

    if (eventFirebase.status) {
      const statusSnap = await getDoc(eventFirebase.status)
      if (statusSnap.exists()) {
        statusData = statusSnap.data() as StatusEventType
      }
    }

    const eventBase: EventBaseType = {
      uid,
      client: {
        uid: clientData.uid,
        name: clientData.name,
        ruc: clientData.ruc,
      },
      description,
      name,
      amount,
      endDate: eventFirebase.endDate
        ? eventFirebase.endDate
        : moment.unix(date).add(1, 'hour').unix(),
      attachments,
      type: typeData!,
      status: statusData!,
      contacts,
      date,
      deleted,
      createdAt,
      updatedAt,
    }

    return eventBase
  }

  async parseToClientBase(opportunityFirebase: ClientFirebaseType) {
    const {
      uid,
      name,
      ruc,
      email,
      description,
      status,
      location,
      contacts,
      type,
      priority,
      updatedAt,
      createdAt,
      phone,
    } = opportunityFirebase

    let statusData: StatusClientType | null = null

    if (status) {
      const statusSnap = await getDoc(status)
      if (statusSnap.exists()) {
        statusData = statusSnap.data() as StatusClientType
      }
    }

    const opportunityBase: ClientBaseType = {
      uid,
      name,
      ruc,
      type,
      email,
      location,
      description,
      contacts,
      priority,
      status: statusData,
      phone,
      updatedAt,
      createdAt,
    }

    return opportunityBase
  }

  async parseToClient(opportunityFirebase: ClientFirebaseType) {
    const {
      uid,
      name,
      ruc,
      email,
      phone,
      description,
      type,
      status,
      location,
      contacts,
      priority,
      updatedAt,
      createdAt,
      events,
    } = opportunityFirebase

    const eventsData: EventBaseType[] = []

    for (const eventRef of events) {
      const eventFirebaseData = await parseRefToEntity<EventFirebaseType>(eventRef)

      const eventData = this.paseToEventBase(eventFirebaseData)

      eventsData.push(await eventData)
    }

    let statusData: StatusClientType | null = null

    if (status) {
      const statusSnap = await getDoc(status)
      if (statusSnap.exists()) {
        statusData = statusSnap.data() as StatusClientType
      }
    }

    const opportunityData: ClientType = {
      uid,
      name,
      ruc,
      email,
      phone,
      type,
      location,
      description,
      status: statusData,
      contacts,
      priority,
      updatedAt,
      createdAt,
      events: eventsData,
    }

    return opportunityData
  }

  async getClients(params: GetClientsType) {
    try {
      const collectionRef = collection(this.db, Collections.CLIENTS.NAME)

      const queries: QueryConstraint[] = []

      if (params.cursorId) {
        const docRef = doc(collectionRef, params.cursorId)
        queries.push(startAfter(docRef))
        params.limit && queries.push(limit(params.limit))
      }

      queries.push(orderBy('updatedAt', params.orderDir ?? 'desc'))

      const q = query(collectionRef, ...queries)

      const opportunitiesSnap = await getDocs(q)

      const opportunitiesData = opportunitiesSnap.docs.map((doc) =>
        doc.data(),
      ) as ClientFirebaseType[]

      const opportunitiesBase: ClientBaseType[] = []

      for (const client of opportunitiesData) {
        opportunitiesBase.push(await this.parseToClientBase(client))
      }

      return opportunitiesBase
    } catch (error) {
      Logger.error('Error getting opportunities', error)
      throw ErrorService.get(ErrorCodes.ERROR_GETTING_CLIENTS)
    }
  }

  async getClientById(id: string) {
    try {
      const docRef = doc(this.db, Collections.CLIENTS.NAME, id)

      const opportunitySnap = await getDoc(docRef)

      const opportunityFirebaseData = opportunitySnap.data() as ClientFirebaseType

      return this.parseToClientBase(opportunityFirebaseData)
    } catch (error) {
      Logger.error('Error getting client by id', error)
      throw ErrorService.get(ErrorCodes.ERROR_GETTING_CLIENTS)
    }
  }

  async createClient(data: CreateClientType) {
    try {
      const currentTimestamp = moment().unix()

      let statusRef = null

      if (data.status) {
        const docRef = doc(this.db, Collections.CLIENT_STATUS.NAME, data.status.uid)

        const statusSnap = await getDoc(docRef)

        if (!statusSnap.exists()) {
          throw new Error('Status not found')
        }

        statusRef = docRef
      }

      const newClient: ClientFirebaseType = {
        ...data,
        uid: uuidv4(),
        updatedAt: currentTimestamp,
        createdAt: currentTimestamp,
        events: [],
        status: statusRef,
      }

      await setDoc(doc(this.db, Collections.CLIENTS.NAME, newClient.uid), newClient)

      const clientData: ClientBaseType = {
        uid: newClient.uid,
        name: newClient.name,
        ruc: newClient.ruc,
        type: newClient.type,
        phone: newClient.phone,
        email: newClient.email,
        status: data.status,
        location: newClient.location,
        description: newClient.description,
        contacts: newClient.contacts,
        priority: newClient.priority,
        updatedAt: newClient.updatedAt,
        createdAt: newClient.createdAt,
      }

      return clientData
    } catch (error) {
      Logger.error('Error creating client', error)
      throw ErrorService.get(ErrorCodes.ERROR_CREATING_CLIENT)
    }
  }

  async updateClient(data: UpdateClientType) {
    try {
      const updateClient: Partial<ClientFirebaseType> = {}

      let statusUpdated = false

      if (data.name) {
        updateClient.name = data.name
      }

      if (data.ruc) {
        updateClient.ruc = data.ruc
      }

      if (data.email) {
        updateClient.email = data.email
      }

      if (data.location) {
        updateClient.location = data.location
      }

      if (data.contacts) {
        updateClient.contacts = data.contacts
      }

      if (data.priority) {
        updateClient.priority = data.priority
      }

      if (data.phone) {
        updateClient.phone = data.phone
      }

      if (data.description) {
        updateClient.description = data.description
      }

      if (data.type) {
        updateClient.type = data.type
      }

      if (data.status) {
        const docRef = doc(this.db, Collections.CLIENT_STATUS.NAME, data.status.uid)

        const statusSnap = await getDoc(docRef)

        if (!statusSnap.exists()) {
          throw new Error('Status not found')
        }

        updateClient.status = docRef
        statusUpdated = true
      }

      if (Object.keys(updateClient).length === 0) {
        throw ErrorService.get(ErrorCodes.ERROR_NOT_FIELDS_TO_UPDATE)
      }

      const docRef = doc(this.db, Collections.CLIENTS.NAME, data.uid)

      await updateDoc(docRef, updateClient)

      if (statusUpdated && data.status) {
        const user = await this.userService.getUser()

        const statusUpdateData: StatusUpdateClientType = {
          uid: uuidv4(),
          updateBy: user,
          clientId: data.uid,
          updatedAt: moment().unix(),
          status: {
            color: data.status.color,
            name: data.status.name,
            uid: data.status.uid,
          },
        }

        const statusUpdateRef = doc(
          this.db,
          Collections.CLIENT_STATUS_UPDATES.NAME,
          statusUpdateData.uid,
        )

        await setDoc(statusUpdateRef, statusUpdateData)
      }
    } catch (error) {
      Logger.error('Error updating client', error)
      throw ErrorService.get(ErrorCodes.ERROR_UPDATING_CLIENT)
    }
  }

  async getStatusHistory(clientId: string): Promise<StatusUpdateClientType[]> {
    try {
      const collectionRef = collection(this.db, Collections.CLIENT_STATUS_UPDATES.NAME)

      const q = query(
        collectionRef,
        where('clientId', '==', clientId),
        orderBy('updatedAt', 'desc'),
      )

      const statusSnap = await getDocs(q)

      const statusData = statusSnap.docs.map((doc) => doc.data()) as StatusUpdateClientType[]

      return statusData
    } catch (error) {
      Logger.error('Error getting client status history', error)
      throw new Error('Error getting client status history')
    }
  }

  async deleteClient(id: string) {
    try {
      const docRef = doc(this.db, Collections.CLIENTS.NAME, id)

      await deleteDoc(docRef)
    } catch (error) {
      Logger.error('Error deleting client', error)
      throw ErrorService.get(ErrorCodes.ERROR_DELETING_CLIENT)
    }
  }

  async deleteClients(ids: string[]) {
    try {
      const batch = writeBatch(this.db)

      ids.forEach((id) => {
        const docRef = doc(this.db, Collections.CLIENTS.NAME, id)

        batch.delete(docRef)
      })

      await batch.commit()
    } catch (error) {
      Logger.error('Error deleting opportunities', error)
      throw ErrorService.get(ErrorCodes.ERROR_DELETING_CLIENTS)
    }
  }

  //Events
  async getEvents(params: GetEventsType) {
    try {
      const collectionRef = collection(this.db, Collections.EVENTS.NAME)

      const queries: QueryConstraint[] = []

      if (params.clientID) {
        queries.push(where('client', '==', doc(this.db, Collections.CLIENTS.NAME, params.clientID)))
      }

      if (params.cursorId) {
        const docRef = doc(collectionRef, params.cursorId)
        queries.push(startAfter(docRef))
        params.limit && queries.push(limit(params.limit))
      }

      queries.push(orderBy('updatedAt', params.orderDir ?? 'desc'))

      const q = query(collectionRef, ...queries)

      const eventsSnap = await getDocs(q)

      const eventsData = eventsSnap.docs.map((doc) => doc.data()) as EventFirebaseType[]

      const eventsBase: EventBaseType[] = []

      for (const event of eventsData) {
        eventsBase.push(await this.paseToEventBase(event))
      }

      return eventsBase
    } catch (error) {
      Logger.error('Error getting events', error)
      throw ErrorService.get(ErrorCodes.ERROR_GETTING_EVENTS)
    }
  }

  async createEvent(params: CreateEventType) {
    try {
      const newEventBase: EventBaseType = await runTransaction(this.db, async (transaction) => {
        const { clientID, data } = params

        const clientRef = doc(this.db, Collections.CLIENTS.NAME, clientID)

        const clientSnap = await transaction.get(clientRef)

        const clientData = clientSnap.data() as ClientFirebaseType

        const currentTimestamp = moment().unix()

        let typeRef = null

        if (data.type) {
          const docRef = doc(this.db, Collections.EVENT_TYPE.NAME, data.type.uid)

          const typeSnap = await transaction.get(docRef)

          if (!typeSnap.exists()) {
            throw new Error('Event type not found')
          }

          typeRef = docRef
        }

        let statusRef = null
        if (data.status) {
          const docRef = doc(this.db, Collections.EVENT_STATUS.NAME, data.status.uid)
          const statusSnap = await transaction.get(docRef)
          if (!statusSnap.exists()) {
            throw new Error('Status not found')
          }
          statusRef = docRef
        }

        const newEvent: EventFirebaseType = {
          ...data,
          uid: uuidv4(),
          type: typeRef!,
          status: statusRef!,
          updatedAt: currentTimestamp,
          createdAt: currentTimestamp,
          client: doc(this.db, Collections.CLIENTS.NAME, clientID),
        }

        transaction.set(doc(this.db, Collections.EVENTS.NAME, newEvent.uid), newEvent)

        const events = clientData.events ?? []

        events.push(doc(this.db, Collections.EVENTS.NAME, newEvent.uid))

        transaction.update(clientRef, { events })

        return this.paseToEventBase(newEvent)
      })

      return newEventBase
    } catch (error) {
      Logger.error('Error creating event', error)
      throw ErrorService.get(ErrorCodes.ERROR_CREATING_EVENT)
    }
  }

  async updateEvent(params: UpdateEventType) {
    try {
      const { clientID, data } = params

      await runTransaction(this.db, async (transaction) => {
        const docRef = doc(this.db, Collections.EVENTS.NAME, data.uid!)

        const updateEvent: Partial<EventFirebaseType> = {}

        if (data.name) {
          updateEvent.name = data.name
        }

        if (data.description) {
          updateEvent.description = data.description
        }

        if (data.amount) {
          updateEvent.amount = data.amount
        }

        if (data.attachments) {
          updateEvent.attachments = data.attachments
        }

        if (data.date) {
          updateEvent.date = data.date
        }

        if (data.endDate) {
          updateEvent.endDate = data.endDate
        }

        if (data.contacts) {
          updateEvent.contacts = data.contacts
        }

        if (data.type) {
          const docRefType = doc(this.db, Collections.EVENT_TYPE.NAME, data.type.uid)

          const typeSnap = await transaction.get(docRefType)

          if (!typeSnap.exists()) {
            throw new Error('Event type not found')
          }

          updateEvent.type = docRefType
        }

        if (data.status) {
          const docRefStatus = doc(this.db, Collections.EVENT_STATUS.NAME, data.status.uid)

          const statusSnap = await transaction.get(docRefStatus)

          if (!statusSnap.exists()) {
            throw new Error('Status not found')
          }

          updateEvent.status = docRefStatus
        }

        if (Object.keys(updateEvent).length === 0) {
          throw ErrorService.get(ErrorCodes.ERROR_NOT_FIELDS_TO_UPDATE)
        }

        transaction.update(docRef, updateEvent)

        const clientRef = doc(this.db, Collections.CLIENTS.NAME, clientID)

        transaction.update(clientRef, { updatedAt: moment().unix() })
      })
    } catch (error) {
      Logger.error('Error updating event', error)
      throw ErrorService.get(ErrorCodes.ERROR_UPDATING_EVENT)
    }
  }

  async deleteEvent(id: string) {
    try {
      await runTransaction(this.db, async (transaction) => {
        const docRef = doc(this.db, Collections.EVENTS.NAME, id)

        const eventSnap = await transaction.get(docRef)

        const event = eventSnap.data() as EventFirebaseType

        const clientSnap = await transaction.get(event.client)

        const clientData = clientSnap.data() as ClientFirebaseType

        const events = clientData.events ?? []

        const index = events.findIndex((e) => e.id === id)

        if (index !== -1) {
          events.splice(index, 1)
        }

        transaction.update(event.client, { events })

        transaction.update(docRef, { deleted: true, updatedAt: moment().unix() })
      })
    } catch (error) {
      Logger.error('Error deleting event', error)
      throw ErrorService.get(ErrorCodes.ERROR_DELETING_EVENT)
    }
  }

  async deleteEvents(params: DeleteEventsType) {
    try {
      const { uids } = params

      await runTransaction(this.db, async (transaction) => {
        const eventSnaps: DocumentSnapshot[] = []

        const clientIds = []

        for (const id of uids) {
          const docRef = doc(this.db, Collections.EVENTS.NAME, id)

          const eventSnap = await transaction.get(docRef)

          eventSnaps.push(eventSnap)

          if (eventSnap.exists()) {
            const eventData = eventSnap.data() as EventFirebaseType
            if (eventData.client) {
              clientIds.push(eventData.client.id)
            }
          }
        }

        if (eventSnaps.length === 0) {
          throw new Error('No events found to delete')
        }

        for (const clientId of clientIds) {
          const clientRef = doc(this.db, Collections.CLIENTS.NAME, clientId)

          const clientSnap = await transaction.get(clientRef)

          if (!clientSnap.exists()) {
            continue
          }

          const clientData = clientSnap.data() as ClientFirebaseType

          const events = clientData.events ?? []

          const eventsFiltered = events.filter((e) => !uids.includes(e.id))

          transaction.update(clientRef, { events: eventsFiltered })
        }

        for (const eventSnap of eventSnaps) {
          transaction.delete(eventSnap.ref)
        }
      })
    } catch (error) {
      Logger.error('Error deleting events', error)
      throw ErrorService.get(ErrorCodes.ERROR_DELETING_EVENTS)
    }
  }

  async createStatus(params: CreateStatusClientType) {
    try {
      const currentTimestamp = moment().unix()

      const newStatus: StatusClientType = {
        uid: uuidv4(),
        name: params.name,
        color: params.color,
        order: params.order,
        updatedAt: currentTimestamp,
        createdAt: currentTimestamp,
      }

      await setDoc(doc(this.db, Collections.CLIENT_STATUS.NAME, newStatus.uid), newStatus)

      return newStatus
    } catch (error) {
      Logger.error('Error creating client', error)
      throw new Error('Error creating client status')
    }
  }

  async updateStatus(params: StatusClientType) {
    try {
      const { uid, ...data } = params

      const currentTimestamp = moment().unix()
      data.updatedAt = currentTimestamp

      const docRef = doc(this.db, Collections.CLIENT_STATUS.NAME, uid)

      await updateDoc(docRef, data)
    } catch (error) {
      Logger.error('Error updating client status', error)
      throw new Error('Error updating client status')
    }
  }

  async updateOrderStatus(params: StatusClientType[]) {
    try {
      const batch = writeBatch(this.db)

      const currentTimestamp = moment().unix()

      for (const status of params) {
        const docRef = doc(this.db, Collections.CLIENT_STATUS.NAME, status.uid)

        batch.update(docRef, { order: status.order, updatedAt: currentTimestamp })
      }

      await batch.commit()
    } catch (error) {
      Logger.error('Error updating order status', error)
      throw new Error('Error updating order status')
    }
  }

  async deleteStatus(statusId: string) {
    try {
      await runTransaction(this.db, async (transaction) => {
        const docRef = doc(this.db, Collections.CLIENT_STATUS.NAME, statusId)
        const statusSnap = await transaction.get(docRef)
        if (!statusSnap.exists()) {
          throw new Error('Status not found')
        }
        const statusData = statusSnap.data() as StatusClientType

        const clientQuery = query(
          collection(this.db, Collections.CLIENTS.NAME),
          where('status', '==', docRef),
        )
        const clientSnap = await getDocs(clientQuery)

        const clientRefToRemove: DocumentSnapshot[] = []
        if (!clientSnap.empty) {
          clientSnap.forEach((clientDoc) => {
            const clientData = clientDoc.data() as ClientFirebaseType
            if (clientData.status && clientData.status.id === statusId) {
              clientRefToRemove.push(clientDoc)
            }
          })
        }

        const { order } = statusData

        const statusQuery = await getDocs(collection(this.db, Collections.CLIENT_STATUS.NAME))

        const statusToUpdate: DocumentSnapshot[] = []
        statusQuery.forEach((statusDoc) => {
          const statusData = statusDoc.data() as StatusClientType
          if (statusData.order > order) {
            statusToUpdate.push(statusDoc)
          }
        })

        for (const statusDoc of statusToUpdate) {
          const statusData = statusDoc.data() as StatusClientType
          transaction.update(statusDoc.ref, {
            order: statusData.order - 1,
            updatedAt: moment().unix(),
          })
        }

        transaction.delete(docRef)
        if (clientRefToRemove.length === 0) {
          return
        }
        for (const clientDoc of clientRefToRemove) {
          transaction.update(clientDoc.ref, { status: null })
        }
      })
    } catch (error) {
      Logger.error('Error deleting client status', error)
      throw new Error('Error deleting client status')
    }
  }

  async getStatusByClientId(clientId: string) {
    try {
      const clientRef = doc(this.db, Collections.CLIENTS.NAME, clientId)

      const statusSnap = await getDoc(clientRef)

      const statusData = statusSnap.data() as ClientFirebaseType

      if (!statusData.status) {
        return null
      }

      const statusRef = statusData.status

      const statusSnapData = await getDoc(statusRef)

      return statusSnapData.data() as StatusClientType
    } catch (error) {
      Logger.error('Error getting client status by client id', error)
      throw new Error('Error getting client status by client id')
    }
  }

  async getStatus() {
    try {
      const collectionRef = collection(this.db, Collections.CLIENT_STATUS.NAME)

      const statusSnap = await getDocs(collectionRef)

      const statusData = statusSnap.docs.map((doc) => doc.data()) as StatusClientType[]

      return statusData.sort((a, b) => a.order - b.order)
    } catch (error) {
      Logger.error('Error getting client status', error)
      throw new Error('Error getting client status')
    }
  }

  async createEventType(params: CreateEventTypeType) {
    try {
      const currentTimestamp = moment().unix()

      const newEventStatus: EventTypeType = {
        uid: uuidv4(),
        name: params.name,
        color: params.color,
        updatedAt: currentTimestamp,
        createdAt: currentTimestamp,
      }

      await setDoc(doc(this.db, Collections.EVENT_TYPE.NAME, newEventStatus.uid), newEventStatus)

      return newEventStatus
    } catch (error) {
      Logger.error('Error creating event status', error)
      throw new Error('Error creating event status')
    }
  }

  async updateEventType(params: EventTypeType) {
    try {
      const { uid, ...data } = params

      const currentTimestamp = moment().unix()
      data.updatedAt = currentTimestamp

      const docRef = doc(this.db, Collections.EVENT_TYPE.NAME, uid)

      await updateDoc(docRef, data)
    } catch (error) {
      Logger.error('Error updating event status', error)
      throw new Error('Error updating event status')
    }
  }

  async deleteEventType(id: string) {
    try {
      await runTransaction(this.db, async (transaction) => {
        const docRef = doc(this.db, Collections.EVENT_TYPE.NAME, id)

        const statusSnap = await transaction.get(docRef)

        if (!statusSnap.exists()) {
          throw new Error('Event status not found')
        }

        const eventQuery = query(
          collection(this.db, Collections.EVENTS.NAME),
          where('type', '==', docRef),
        )
        const eventSnap = await getDocs(eventQuery)

        if (!eventSnap.empty) {
          eventSnap.forEach((eventDoc) => {
            transaction.update(eventDoc.ref, { type: null })
          })
        }

        transaction.delete(docRef)
      })
    } catch (error) {
      Logger.error('Error deleting event status', error)
      throw new Error('Error deleting event status')
    }
  }

  async getEventTypeOptions() {
    try {
      const collectionRef = collection(this.db, Collections.EVENT_TYPE.NAME)

      const statusSnap = await getDocs(collectionRef)

      const statusData = statusSnap.docs.map((doc) => doc.data()) as EventTypeType[]

      return statusData
    } catch (error) {
      Logger.error('Error getting event types options', error)
      throw new Error('Error getting event types options')
    }
  }

  async createEventStatus(params: CreateEventStatusType): Promise<StatusEventType> {
    try {
      const currentTimestamp = moment().unix()

      const newStatus: StatusEventType = {
        uid: uuidv4(),
        name: params.name,
        color: params.color,
        order: params.order,
        updatedAt: currentTimestamp,
        createdAt: currentTimestamp,
      }

      await setDoc(doc(this.db, Collections.EVENT_STATUS.NAME, newStatus.uid), newStatus)

      return newStatus
    } catch (error) {
      Logger.error('Error creating event status', error)
      throw new Error('Error creating event status')
    }
  }

  async updateEventStatus(params: StatusEventType): Promise<void> {
    try {
      const { uid, ...data } = params

      const currentTimestamp = moment().unix()
      data.updatedAt = currentTimestamp

      const docRef = doc(this.db, Collections.EVENT_STATUS.NAME, uid)

      await updateDoc(docRef, data)
    } catch (error) {
      Logger.error('Error updating event status', error)
      throw new Error('Error updating event status')
    }
  }

  async updateOrderEventStatus(params: StatusEventType[]): Promise<void> {
    try {
      const batch = writeBatch(this.db)

      const currentTimestamp = moment().unix()

      for (const status of params) {
        const docRef = doc(this.db, Collections.EVENT_STATUS.NAME, status.uid)

        batch.update(docRef, { order: status.order, updatedAt: currentTimestamp })
      }

      await batch.commit()
    } catch (error) {
      Logger.error('Error updating order event status', error)
      throw new Error('Error updating order event status')
    }
  }

  async deleteEventStatus(statusId: string): Promise<void> {
    try {
      await runTransaction(this.db, async (transaction) => {
        const docRef = doc(this.db, Collections.EVENT_STATUS.NAME, statusId)
        const statusSnap = await transaction.get(docRef)
        if (!statusSnap.exists()) {
          throw new Error('Event status not found')
        }
        const statusData = statusSnap.data() as StatusEventType

        const eventQuery = query(
          collection(this.db, Collections.EVENTS.NAME),
          where('status', '==', docRef),
        )
        const eventSnap = await getDocs(eventQuery)

        const eventRefToRemove: DocumentSnapshot[] = []
        if (!eventSnap.empty) {
          eventSnap.forEach((eventDoc) => {
            const eventData = eventDoc.data() as EventFirebaseType
            if (eventData.status && eventData.status.id === statusId) {
              eventRefToRemove.push(eventDoc)
            }
          })
        }

        const { order } = statusData

        const statusQuery = await getDocs(collection(this.db, Collections.EVENT_STATUS.NAME))

        const statusToUpdate: DocumentSnapshot[] = []
        statusQuery.forEach((statusDoc) => {
          const statusData = statusDoc.data() as StatusEventType
          if (statusData.order > order) {
            statusToUpdate.push(statusDoc)
          }
        })

        for (const statusDoc of statusToUpdate) {
          const statusData = statusDoc.data() as StatusEventType
          transaction.update(statusDoc.ref, {
            order: statusData.order - 1,
            updatedAt: moment().unix(),
          })
        }

        transaction.delete(docRef)
        if (eventRefToRemove.length === 0) {
          return
        }
        for (const eventDoc of eventRefToRemove) {
          transaction.update(eventDoc.ref, { status: null })
        }
      })
    } catch (error) {
      Logger.error('Error deleting event status', error)
      throw new Error('Error deleting event status')
    }
  }

  async getEventStatusOptions(): Promise<StatusEventType[]> {
    try {
      const collectionRef = collection(this.db, Collections.EVENT_STATUS.NAME)

      const statusSnap = await getDocs(collectionRef)

      const statusData = statusSnap.docs.map((doc) => doc.data()) as StatusEventType[]

      return statusData.sort((a, b) => a.order - b.order)
    } catch (error) {
      Logger.error('Error getting event status options', error)
      throw new Error('Error getting event status options')
    }
  }
}

export default ClientEntityImpl
